import { extensionId, features, marketplaceUrl, pythonSdkUrl, screenshotUrls, useCases } from '../content';
import { HeroCanStream } from '../components/HeroCanStream';

const capabilities: Array<[string, string, string]> = [
  ['Live CAN streaming', 'Stream raw frames in the browser over Wi-Fi or USB without leaving the page.', '/live'],
  ['CAN log analysis', 'Open CANLOG01 captures, filter, and inspect frame rates, IDs, and byte changes.', '/analyzer'],
  ['Signal decoding', 'Match observed CAN IDs against verified community signal records.', '/decode'],
  ['Community signals', 'Contribute decode evidence and reuse signals verified by the Ameriuse team.', '/decode']
];

const flowSteps: Array<[string, string, string]> = [
  ['01', 'Hardware', 'SmartCAN device captures CAN traffic over USB or Wi-Fi.'],
  ['02', 'Live', 'Stream and inspect frames in real time directly in your browser.'],
  ['03', 'Decode', 'Apply community-verified signal hints to interpret raw bytes.'],
  ['04', 'Improve', 'Submit new signals; verified contributions feed the catalog.']
];

export function HomePage() {
  return (
    <main>
      <section className="hero">
        <div className="hero-copy reveal">
          <p className="eyebrow">SmartCAN by Ameriuse</p>
          <h1>AI-powered CAN Bus analysis in seconds</h1>
          <p className="hero-subtitle">
            Stream, analyze, and decode CAN data directly in your browser.
          </p>
          <p className="hero-description">
            SmartCAN combines USB high-rate capture, Wi-Fi live monitoring, browser analysis, and a community-verified signal catalog for embedded and automotive engineers.
          </p>
          <div className="hero-actions">
            <a className="button primary" href="/live">Try Live Demo</a>
            <a className="button secondary" href="/analyzer">Analyze CAN Log</a>
            <a className="button ghost" href="/decode">Community Signals</a>
            <a className="button ghost" href={marketplaceUrl} target="_blank" rel="noreferrer">VS Code Extension</a>
          </div>
          <p className="hero-trust">
            Used for automotive diagnostics, embedded systems, and reverse engineering workflows.
          </p>
          <div className="hero-proof" aria-label="SmartCAN capabilities">
            <span>USB high-rate capture</span>
            <span>Wi-Fi dashboard</span>
            <span>Community signal catalog</span>
          </div>
        </div>
        <div className="hero-visual hero-stream-wrap reveal" aria-label="Simulated SmartCAN live CAN frame stream">
          <HeroCanStream />
        </div>
      </section>

      <section className="section reveal">
        <div className="section-heading">
          <p className="eyebrow">What you can do</p>
          <h2>From raw CAN bytes to understandable signals</h2>
        </div>
        <div className="capability-grid">
          {capabilities.map(([title, body, href]) => (
            <a className="capability-card" key={title} href={href}>
              <span className="capability-arrow" aria-hidden="true">&rarr;</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </a>
          ))}
        </div>
      </section>

      <section className="section reveal">
        <div className="section-heading">
          <p className="eyebrow">How it works</p>
          <h2>Hardware &rarr; Live &rarr; Decode &rarr; Improve</h2>
        </div>
        <ol className="flow-grid">
          {flowSteps.map(([step, title, body]) => (
            <li className="flow-step" key={step}>
              <span className="flow-step-num">{step}</span>
              <h3>{title}</h3>
              <p>{body}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="section reveal">
        <div className="section-heading">
          <p className="eyebrow">Capabilities</p>
          <h2>Built for CAN work beyond a single domain</h2>
        </div>
        <div className="feature-grid">
          {features.map(([title, body]) => (
            <article className="feature-card" key={title}>
              <span className="card-icon" aria-hidden="true"></span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section reveal">
        <div className="section-heading">
          <p className="eyebrow">Screenshots</p>
          <h2>Extension and dashboard workflows</h2>
          <p>Public screenshots show USB high-rate monitoring, Wi-Fi snapshot mode, and CAN filtering/highlight workflows.</p>
        </div>
        <div className="screenshot-grid">
          {screenshotUrls.map((shot) => (
            <article className="screenshot-card" key={shot.src}>
              <img src={shot.src} alt={shot.alt} loading="lazy" />
              <h3>{shot.title}</h3>
            </article>
          ))}
        </div>
      </section>

      <section className="band analyzer-band reveal">
        <div>
          <p className="eyebrow">Browser-based capture analysis</p>
          <h2>Analyze captures in your browser</h2>
          <p>
            Already have a SmartCAN capture file? Open it in the SmartCAN Web Analyzer to inspect CAN bus IDs, ECU frame rates, byte changes, and export clean CSV/JSON files for embedded systems and automotive data review.
          </p>
          <a className="button primary" href="/analyzer">Open Web Analyzer</a>
        </div>
        <div className="analyzer-preview" aria-label="SmartCAN Web Analyzer preview">
          <div><small>Frames</small><strong>48,230</strong></div>
          <div><small>Unique IDs</small><strong>37</strong></div>
          <div><small>Filter</small><strong>0x025</strong></div>
          <div className="preview-row"></div>
          <div className="preview-row short"></div>
          <div className="preview-row"></div>
        </div>
      </section>

      <section className="band live-band reveal">
        <div>
          <p className="eyebrow">Wi-Fi Live Mode</p>
          <h2>Connect to a SmartCAN device over Wi-Fi</h2>
          <p>
            Poll a SmartCAN device on your local network, capture live CAN bus frames in the browser, optionally decode ECU data batches with Decode Cloud, and export the captured session as CSV or JSON for real-time analysis.
          </p>
          <a className="button primary" href="/live">Open Web Live</a>
        </div>
        <div className="analyzer-preview" aria-label="SmartCAN Web Live preview">
          <div><small>Status</small><strong>Connected</strong></div>
          <div><small>FPS</small><strong>42.8</strong></div>
          <div><small>IDs</small><strong>18</strong></div>
          <div className="preview-row"></div>
          <div className="preview-row short"></div>
          <div className="preview-row"></div>
        </div>
      </section>

      <section className="band reveal">
        <div className="mode-grid">
          <div>
            <p className="eyebrow">Transport modes</p>
            <h2>USB for high-rate work. Wi-Fi for fast device visibility.</h2>
          </div>
          <article>
            <h3>USB High Rate</h3>
            <p>Raw binary live streaming is the professional path for real-time analysis, high-rate capture, and low-loss monitoring sessions.</p>
          </article>
          <article>
            <h3>Wi-Fi Snapshot</h3>
            <p>The browser dashboard is designed for quick status, diagnostics, SD file access, and lightweight monitoring from the bench or field.</p>
          </article>
        </div>
      </section>

      <section className="band reveal">
        <div className="mode-grid">
          <div>
            <p className="eyebrow">CAN decoder context</p>
            <h2>Learn how SmartCAN supports CAN bus and ECU analysis</h2>
          </div>
          <article>
            <h3>Signal hints</h3>
            <p>Verified community records help identify candidate CAN decoder signals when matching CAN IDs are observed in Analyzer or Live Mode.</p>
          </article>
          <article>
            <h3>Educational guide</h3>
            <p>New to CAN bus analysis? Read the short guide for how SmartCAN fits automotive, embedded systems, and reverse engineering workflows.</p>
            <div className="inline-actions">
              <a className="button secondary" href="/what-is-smartcan">Read the guide</a>
              <a className="button ghost" href="/best-can-bus-tools">Best CAN Bus tools</a>
              <a className="button ghost" href="/smartcan-vs-alternatives">Compare SmartCAN</a>
              <a className="button ghost" href="/can-bus-decoder-guide">CAN decoder guide</a>
            </div>
          </article>
        </div>
      </section>

      <section className="section split-section reveal">
        <div>
          <p className="eyebrow">Use cases</p>
          <h2>One CAN tool for engineering, diagnostics, and operations</h2>
        </div>
        <div className="usecase-grid">
          {useCases.map((item) => <span key={item}>{item}</span>)}
        </div>
      </section>

      <section className="section two-column reveal">
        <article className="status-panel">
          <p className="eyebrow">Product status</p>
          <h2>SmartCAN Pro Dev Kit is in active development.</h2>
          <p>The VS Code Extension is live, and the Python SDK is public on GitHub for local/dev install and automation workflows.</p>
          <a className="button secondary" href="/waitlist">Join Dev Kit waitlist</a>
        </article>
        <article className="code-panel">
          <p className="eyebrow">Python SDK - local/dev install</p>
          <h2>Script SmartCAN workflows</h2>
          <pre><code>{`git clone https://github.com/ameriuse/smartcan-python-sdk
cd smartcan-python-sdk
pip install -e .

from smartcan import SmartCAN
dev = SmartCAN.auto()
print(dev.status())
for frame in dev.live():
    print(frame)`}</code></pre>
          <p className="panel-note">Future PyPI command: <code>pip install smartcan coming soon</code></p>
        </article>
      </section>

      <section className="band sdk-band reveal">
        <div>
          <p className="eyebrow">Python SDK</p>
          <h2>Automate SmartCAN workflows using Python.</h2>
          <p>The SmartCAN Python SDK enables USB and Wi-Fi device control, live monitoring and capture, automation, and scripting.</p>
          <a className="button primary" href={pythonSdkUrl} target="_blank" rel="noreferrer">View on GitHub</a>
        </div>
        <pre><code>{`git clone https://github.com/ameriuse/smartcan-python-sdk
cd smartcan-python-sdk
pip install -e .`}</code></pre>
      </section>

      <section className="band extension-band reveal">
        <div>
          <p className="eyebrow">VS Code Extension</p>
          <h2>High-rate USB live view for developers</h2>
          <p>Use the extension for live tables, capture inspection, signal discovery, and exports. Install it from the Visual Studio Marketplace or use the VS Code CLI.</p>
          <pre><code>{`ext install ${extensionId}
code --install-extension ${extensionId}`}</code></pre>
          <div className="inline-actions">
            <a className="button secondary" href={marketplaceUrl} target="_blank" rel="noreferrer">Marketplace</a>
            <a className="button ghost" href="/docs/extension">Extension docs</a>
          </div>
        </div>
        <div className="extension-shot" aria-label="VS Code extension screenshot mockup">
          <div className="sidebar"></div>
          <div className="editor">
            <div className="editor-tab">SmartCAN Live</div>
            <div className="editor-row wide"></div>
            <div className="editor-row"></div>
            <div className="editor-row short"></div>
            <div className="editor-grid"></div>
          </div>
        </div>
      </section>

      <section className="section safety-section reveal">
        <div>
          <p className="eyebrow">Safety posture</p>
          <h2>Passive by default</h2>
          <p>
            SmartCAN Pro is designed for passive CAN monitoring. Default firmware uses ListenOnly mode. It does not transmit or control vehicle or device systems unless explicitly configured by advanced users.
          </p>
          <div className="account-preview">
            <p className="eyebrow">Account / Login coming soon</p>
            <h3>Beta access without fake auth</h3>
            <p>Future accounts are planned for hardware beta access, device registration, download history, and SDK keys if needed later.</p>
          </div>
        </div>
        <div className="download-panel">
          <p className="eyebrow">Downloads</p>
          <h3>Current install paths</h3>
          <a href={marketplaceUrl} target="_blank" rel="noreferrer">VS Code Extension: Marketplace</a>
          <a href={pythonSdkUrl} target="_blank" rel="noreferrer">Python SDK: GitHub</a>
          <code>git clone https://github.com/ameriuse/smartcan-python-sdk && cd smartcan-python-sdk && pip install -e .</code>
          <span>Firmware: coming soon</span>
          <span>Sample CANLOG01: TODO placeholder</span>
          <a className="button primary" href="/docs/downloads">View downloads</a>
        </div>
      </section>
    </main>
  );
}
